import { action, makeObservable, observable } from "mobx";

import type { Filter } from "@/components/StructuredSearch/config";
import type { CompleteDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";

export interface AnalyticsQueryState {
  filters: Filter[];
  dateRange: CompleteDateRange;
  q: string;
}

const cloneDateRange = (dateRange: CompleteDateRange): CompleteDateRange => ({
  from: new Date(dateRange.from),
  to: new Date(dateRange.to),
});

const cloneFilter = (filter: Filter): Filter => ({
  ...filter,
  value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
  multiValueDisplay: filter.multiValueDisplay ? [...filter.multiValueDisplay] : undefined,
});

export const createAnalyticsQueryState = ({
  filters,
  dateRange,
  q,
}: AnalyticsQueryState): AnalyticsQueryState => ({
  filters: filters.map(cloneFilter),
  dateRange: cloneDateRange(dateRange),
  q,
});

export const serializeAnalyticsQueryState = (queryState: AnalyticsQueryState): string =>
  JSON.stringify({
    filters: queryState.filters.map(filter => ({
      field: filter.field,
      type: filter.type,
      operator: filter.operator,
      value: filter.value,
      valueDisplay: filter.valueDisplay,
      multiValueDisplay: filter.multiValueDisplay,
      nestedKey: filter.nestedKey,
    })),
    dateRange: {
      from: queryState.dateRange.from.toISOString(),
      to: queryState.dateRange.to.toISOString(),
    },
    q: queryState.q,
  });

export const areAnalyticsQueriesEqual = (
  leftQueryState: AnalyticsQueryState,
  rightQueryState: AnalyticsQueryState,
): boolean =>
  serializeAnalyticsQueryState(leftQueryState) === serializeAnalyticsQueryState(rightQueryState);

const MAX_ANALYTICS_QUERY_HISTORY_STATES = 20;

export class AnalyticsQueryStore {
  private _queryState: AnalyticsQueryState;

  private _queryHistory: AnalyticsQueryState[] = [];

  private _isSearchEndpointRunning = false;

  private _shouldDeferNextAutomaticQuerySync = false;

  private readonly _getCurrentQueryState: () => AnalyticsQueryState;

  constructor(getCurrentQueryState: () => AnalyticsQueryState) {
    this._getCurrentQueryState = getCurrentQueryState;
    this._queryState = createAnalyticsQueryState(this._getCurrentQueryState());

    makeObservable<
      AnalyticsQueryStore,
      "_queryState" | "_queryHistory" | "_isSearchEndpointRunning"
    >(this, {
      _queryState: observable.ref,
      _queryHistory: observable.ref,
      _isSearchEndpointRunning: observable,
      runQuery: action,
      restoreQueryState: action,
      syncAutomaticQuery: action,
      deferNextAutomaticQuerySync: action,
      setIsSearchEndpointRunning: action,
    });
  }

  get queryState() {
    return this._queryState;
  }

  get previousQueryState() {
    return this._queryHistory[this._queryHistory.length - 1] ?? null;
  }

  get queryHistoryCount() {
    return this._queryHistory.length;
  }

  get isSearchEndpointRunning() {
    return this._isSearchEndpointRunning;
  }

  runQuery = () => {
    this._shouldDeferNextAutomaticQuerySync = false;
    const nextQueryState = createAnalyticsQueryState(this._getCurrentQueryState());
    if (!areAnalyticsQueriesEqual(this._queryState, nextQueryState)) {
      this._pushQueryHistoryState(this._queryState);
    }
    this._queryState = nextQueryState;
  };

  restoreQueryState = (
    queryState: AnalyticsQueryState,
    { consumeHistory = false }: { consumeHistory?: boolean } = {},
  ) => {
    this._shouldDeferNextAutomaticQuerySync = false;
    if (consumeHistory && this.previousQueryState) {
      this._queryHistory = areAnalyticsQueriesEqual(this.previousQueryState, queryState)
        ? this._queryHistory.slice(0, -1)
        : this._queryHistory;
    }
    this._queryState = createAnalyticsQueryState(queryState);
  };

  syncAutomaticQuery = () => {
    if (this._shouldDeferNextAutomaticQuerySync) {
      this._shouldDeferNextAutomaticQuerySync = false;
      return;
    }

    const nextQueryState = createAnalyticsQueryState(this._getCurrentQueryState());
    if (!areAnalyticsQueriesEqual(this._queryState, nextQueryState)) {
      this._pushQueryHistoryState(this._queryState);
    }
    this._queryState = nextQueryState;
  };

  private _pushQueryHistoryState = (queryState: AnalyticsQueryState) => {
    const clonedQueryState = createAnalyticsQueryState(queryState);
    const lastHistoryState = this.previousQueryState;
    if (lastHistoryState && areAnalyticsQueriesEqual(lastHistoryState, clonedQueryState)) {
      return;
    }

    this._queryHistory = [...this._queryHistory, clonedQueryState].slice(
      -MAX_ANALYTICS_QUERY_HISTORY_STATES,
    );
  };

  deferNextAutomaticQuerySync = () => {
    this._shouldDeferNextAutomaticQuerySync = true;
  };

  setIsSearchEndpointRunning = (isRunning: boolean) => {
    this._isSearchEndpointRunning = isRunning;
  };
}
